import dotProp from 'dot-prop-immutable'
import findIndex from 'lodash/findIndex'
import deleteRecord from '@/utils/delete-record'
import updateRecord from '@/utils/update-record'

// API resources
import apiAccounts from '@/api/modules/accounts'
import apiPortfolios from '@/api/modules/portfolios'
import apiUser from '@/api/modules/user'

// Mutations
import {
  ADD_ACCOUNTS_LIST,
  ADD_ACCOUNT,
  DELETE_ACCOUNT,
  ADD_ACCOUNTS_REPARTITION,
  DELETE_ACCOUNTS_REPARTITION,
  UPDATE_ACCOUNTS_LIST,
  UPDATE_ACCOUNT_PORTFOLIO,
  RESET_MODULE
} from '@/store/mutation-types'

// Default state
const getDefaultState = () => {
  return {
    list: [],
    waiting: {},
    accountsRepartition: []
  }
}
const state = getDefaultState()

// -----------------------------------------------------------------------------

const actions = {
  add (context, data) {
    return apiUser.addAccount(data)
      .catch((err) => {
        return Promise.reject(err)
      })
  },

  delete (context, id) {
    return apiAccounts.delete(id)
      .then(() => {
        context.dispatch('fetchRepartition')
        context.dispatch('contracts/fetchRepartition', null, { root: true })
        return context.commit(DELETE_ACCOUNT, id)
      })
  },

  fetch (context, options = {}) {
    const id = options.id || undefined
    const cached = options.cached || false
    const { list } = context.state

    if (cached && list.length) {
      return Promise.resolve(list)
    }

    return apiUser.fetchAccounts(id)
      .then(data => {
        context.commit(ADD_ACCOUNTS_LIST, data)
      })
  },

  fetchById ({ state, commit }, { id, cached = false }) {
    const { list } = state
    const cachedAccount = list.find(account => parseInt(account.id) === parseInt(id))

    if (cached && cachedAccount) {
      return Promise.resolve()
    }

    return apiUser.fetchAccount(id)
      .then(data => {
        commit(ADD_ACCOUNT, data)
      })
      .catch((err) => {
        return Promise.reject(err)
      })
  },

  fetchRepartition (context, cache) {
    if (cache && context.state.accountsRepartition.length) {
      return Promise.resolve()
    }
    return apiUser.fetchAccountsRepartition()
      .then(({ data }) => context.commit(ADD_ACCOUNTS_REPARTITION, data))
  },

  synchronize ({ commit }, accountId) {
    return apiAccounts.synchronize(accountId)
      .then(data => {
        commit(UPDATE_ACCOUNTS_LIST, data)
        return Promise.resolve()
      })
  },

  // PUTs new portfolio data on the server then commits in store
  updatePortfolio (context, { portfolioId, data, update = true }) {
    return apiPortfolios.updatePortfolio(portfolioId, data)
      .then((response) => {
        context.commit(UPDATE_ACCOUNT_PORTFOLIO, {
          accountId: response.accountId,
          portfolioId,
          data
        })
      })
  },

  resetRepartition (context) {
    context.commit(DELETE_ACCOUNTS_REPARTITION)
  },

  resetState (context) {
    context.commit(RESET_MODULE)
  }
}

const mutations = {
  [ADD_ACCOUNTS_LIST] (state, data) {
    state.list = data
  },

  [ADD_ACCOUNT] (state, data) {
    const index = findIndex(state.list, { id: data.id })
    if (index >= 0) {
      state.list = [
        ...state.list.slice(0, index),
        data,
        ...state.list.slice(index + 1)
      ]
    } else {
      state.list = [
        ...state.list,
        data
      ]
    }
  },

  [ADD_ACCOUNTS_REPARTITION] (state, data) {
    state.accountsRepartition = data
  },

  [DELETE_ACCOUNTS_REPARTITION] (state) {
    state.accountsRepartition = []
  },

  [DELETE_ACCOUNT] ({ list }, id) {
    deleteRecord(list, id)
  },

  [UPDATE_ACCOUNTS_LIST] ({ list }, data) {
    updateRecord(list, data, false)
  },

  [UPDATE_ACCOUNT_PORTFOLIO] (state, { accountId, portfolioId, data }) {
    const accountIndex = findIndex(state.list, { id: accountId })
    if (accountIndex > -1) {
      const portfolioIndex = findIndex(state.list[accountIndex].portfolios, { id: portfolioId })
      if (portfolioIndex > -1) {
        state.list = dotProp.merge(state.list, `${accountIndex}.portfolios.${portfolioIndex}`, data)
      }
    }
  },

  [RESET_MODULE] (state) {
    Object.assign(state, getDefaultState())
  }
}

const getters = {
  getById ({ list }) {
    return id => list.find(account => account.id === id)
  },

  getByConnexionId ({ list }) {
    return connexionId => list.find(account => account.connexionId === connexionId)
  },

  getPortfoliosByKind: ({ list }) => (kind) => {
    const portfolios = []
    for (const account of list) {
      portfolios.push(...(account.portfolios.filter(portfolio => portfolio.kind === kind && portfolio.status !== 'hidden')))
    }
    return portfolios
  },

  hasAccountErrors ({ list }) {
    return list.some(a => {
      return a.state === 'error'
    })
  },

  getAccountByPortfolioId: ({ list }) => (portfolioId) => {
    return list.find(account => account.portfolios.some((portfolio) => parseInt(portfolio.id) === parseInt(portfolioId)))
  }
}

export default () => ({
  namespaced: true,
  state,
  actions,
  mutations,
  getters
})
